import { useState, useEffect } from "react";
import { X, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Exit-intent 10% off newsletter popup.
 * - Triggers on mouseleave from the viewport top (desktop)
 * - Falls back to a 90s delay on mobile/touch (no mouseleave)
 * - Suppressed after dismissal, subscription, or if the user is already subscribed
 * - Feeds captured email into Brevo via /api/newsletter/subscribe
 */
export default function NewsletterPopup() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("untamed_popup_dismissed")) return;

    let mobileTimer;
    let armed = false;

    const trigger = () => {
      if (armed) return;
      armed = true;
      setShow(true);
    };

    const onMouseLeave = (e) => {
      // Only fire when mouse leaves via the top of the viewport
      if (e.clientY <= 0) trigger();
    };

    // Detect touch device — no mouseleave; use time-based fallback
    const isTouch = window.matchMedia && window.matchMedia("(hover: none)").matches;
    if (isTouch) {
      mobileTimer = setTimeout(trigger, 90000); // 90s on mobile
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!consent) { toast.error("Please tick the consent box"); return; }
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email: email.trim().toLowerCase(), name: name.trim(), marketing_consent: true });
      setDone(true);
      toast.success("Welcome to the Untamed family!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong";
      if (msg.includes("already")) { toast.info("You're already subscribed — use code WELCOME10 at checkout."); setDone(true); }
      else toast.error(msg);
    } finally { setLoading(false); }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem("untamed_popup_dismissed", "1");
  };

  const copyCode = () => {
    navigator.clipboard?.writeText("WELCOME10").then(() => {
      setCopied(true);
      toast.success("Code WELCOME10 copied!");
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => toast.info("Code: WELCOME10"));
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={handleDismiss}>
      <div data-testid="newsletter-popup" className="bg-[#171717] border border-[#F7931E]/30 max-w-md w-full relative animate-slide-up" onClick={e => e.stopPropagation()}>
        <button data-testid="popup-dismiss" onClick={handleDismiss} className="absolute top-3 right-3 text-[#525252] hover:text-white transition-colors z-10"><X size={20} /></button>

        <div className="bg-[#F7931E] p-4 text-center">
          <p className="text-black font-black uppercase tracking-tighter text-2xl font-heading">Wait — 10% Off</p>
          <p className="text-black/70 text-sm">On your first Untamed Gear order</p>
        </div>

        {done ? (
          <div className="p-6 text-center">
            <p className="text-white font-bold text-lg mb-1">You&apos;re in! 🎉</p>
            <p className="text-[#A3A3A3] text-sm mb-4">Use this code at checkout for 10% off:</p>
            <button
              data-testid="popup-copy-code"
              onClick={copyCode}
              className="w-full bg-black border-2 border-dashed border-[#F7931E] py-3 px-4 flex items-center justify-center gap-3 hover:bg-[#F7931E]/10 transition-colors group"
            >
              <span className="text-[#F7931E] font-mono font-black text-xl tracking-widest">WELCOME10</span>
              {copied ? <Check size={18} className="text-[#F7931E]" /> : <Copy size={18} className="text-[#F7931E] group-hover:scale-110 transition-transform" />}
            </button>
            <p className="text-[#525252] text-[10px] mt-3">A copy has been sent to your inbox. Happy shopping!</p>
          </div>
        ) : (
          <div className="p-6">
            <p className="text-[#A3A3A3] text-sm mb-4 text-center">
              Drop your email and we&apos;ll send you a <span className="text-[#F7931E] font-bold">WELCOME10</span> code plus first-look at new drops.
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input data-testid="popup-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
                className="w-full bg-black border border-[#262626] text-white px-3 py-2.5 text-sm outline-none focus:border-[#F7931E] placeholder:text-[#525252]" />
              <input data-testid="popup-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email" required
                className="w-full bg-black border border-[#262626] text-white px-3 py-2.5 text-sm outline-none focus:border-[#F7931E] placeholder:text-[#525252]" />
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  data-testid="popup-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 accent-[#F7931E] cursor-pointer flex-shrink-0"
                />
                <span className="text-[#A3A3A3] text-[10px] leading-snug">
                  I agree to receive marketing emails and accept the{" "}
                  <a href="/policies" target="_blank" rel="noopener noreferrer" className="text-[#F7931E] underline">Privacy Policy (POPIA)</a>.
                </span>
              </label>
              <button data-testid="popup-submit" type="submit" disabled={loading || !consent}
                className="w-full bg-[#F7931E] text-black font-bold uppercase tracking-wide py-3 text-sm hover:bg-[#D97715] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                {loading ? "Signing up..." : "Send Me the Code"}
              </button>
            </form>

            <p className="text-[#363636] text-[10px] text-center mt-3">No spam. Unsubscribe anytime.</p>
          </div>
        )}
      </div>
    </div>
  );
}
